import { saveAs } from "file-saver";
import JSZip from "jszip";

import apiService from "../services/apiService";

export const downloadPhotosAsZip = async (
  photos,
  eventName = "Event Photos"
) => {
  // Genera ZIP local mediante presigned URLs (sin abrir pestañas)
  if (!Array.isArray(photos) || photos.length === 0) {
    return {
      success: false,
      error: "No photos to download",
      method: "client-zip",
    };
  }
  const attemptClientZip = async () => {
    const zip = new JSZip();
    let loadedCount = 0;
    const totalPhotos = photos.length;

    const loadingToast = document.createElement("div");
    loadingToast.className = "download-loading-toast";
    loadingToast.innerHTML = `
      <div class=\"download-progress\">
        <div class=\"download-spinner\"></div>
        <span>Preparing download... (0/${totalPhotos})</span>
      </div>
    `;
    document.body.appendChild(loadingToast);

    const updateProgress = (loaded) => {
      loadingToast.querySelector(
        "span"
      ).textContent = `Preparing download... (${loaded}/${totalPhotos})`;
    };

    for (let i = 0; i < photos.length; i++) {
      const photo = photos[i];
      try {
        // Presigned URL para esta foto
        const presignedResp = await apiService.makeRequest(
          `/photo-download-url/?id=${photo.id}`,
          { method: "GET" }
        );
        if (!presignedResp.success)
          throw new Error(presignedResp.message || "Failed presigned URL");
        const response = await fetch(presignedResp.url);
        if (!response.ok)
          throw new Error(
            `Failed to fetch presigned ${photo.id} (HTTP ${response.status})`
          );
        const blob = await response.blob();
        const cleanPath = photo.url.split("?")[0];
        const extension = (cleanPath.split(".").pop() || "jpg").slice(0, 5);
        const uploaderName = eventName
          ? eventName.replace(/\s+/g, "_")
          : "event";
        let d = new Date(photo.uploadDate || photo.created);
        if (isNaN(d.getTime())) d = new Date();
        const timestamp = d.toISOString().slice(0, 10);
        const filename = `${i + 1}_${uploaderName}_${timestamp}.${extension}`;
        zip.file(filename, blob);
        loadedCount++;
        updateProgress(loadedCount);
      } catch (err) {
        // Abortamos el proceso completo ante el primer error
        throw err;
      }
    }

    loadingToast.querySelector("span").textContent = "Creating zip file...";
    const zipBlob = await zip.generateAsync(
      {
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      },
      (metadata) => {
        const progress = Math.round(metadata.percent);
        loadingToast.querySelector(
          "span"
        ).textContent = `Creating zip file... ${progress}%`;
      }
    );
    const ts = new Date().toISOString().slice(0, 10);
    const zipFilename = `${
      eventName ? eventName.replace(/\s+/g, "_") : "Event"
    }_${ts}.zip`;
    saveAs(zipBlob, zipFilename);
    document.body.removeChild(loadingToast);
    return {
      success: true,
      filename: zipFilename,
      photoCount: loadedCount,
      method: "client-zip",
    };
  };

  try {
    return await attemptClientZip();
  } catch (err) {
    console.warn("Client zip failed, no fallback to abrir pestañas:", err);
    return {
      success: false,
      error: err.message || String(err),
      method: "zip-failed",
    };
  }
};

export const downloadSinglePhoto = async (photo, eventName) => {
  const cleanPath = (photo.url || "").split("?")[0];
  let extension = cleanPath.split(".").pop() || "jpg";
  if (extension.length > 5) extension = "jpg";
  const safeEvent = eventName ? eventName.replace(/\s+/g, "_") : "Event";
  let d = new Date(photo.uploadDate || photo.created);
  if (isNaN(d.getTime())) d = new Date();
  const datePart = d.toISOString().slice(0, 10);
  const filename = `${safeEvent}_${datePart}_${
    photo.id || "photo"
  }.${extension}`;
  try {
    const presignedResp = await apiService.makeRequest(
      `/photo-download-url/?id=${photo.id}`,
      { method: "GET" }
    );
    if (!presignedResp.success)
      throw new Error(presignedResp.message || "Failed presigned URL");
    const res = await fetch(presignedResp.url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    saveAs(blob, filename);
    return { success: true, filename, method: "blob" };
  } catch (err) {
    console.warn(
      "Single photo presigned fetch failed, sin abrir nueva pestaña:",
      err
    );
    return {
      success: false,
      filename,
      method: "failed",
      error: err.message || String(err),
    };
  }
};
